/**
 * The compact "MockSpec" — the small, hand-owned intent shape that the AI mock generator targets
 * and the dev panel persists. The model decides *what* contracts a persona has; `expandMockSpec`
 * turns this into valid GraphQL resolvers, so the model never needs to know the full schema shape.
 *
 * Dev-only: consumed exclusively behind `isDevPanelEnabled()`. Validation is hand-rolled (no runtime
 * dependency) so nothing extra ships in the production bundle — this module is reachable from the
 * always-bundled `@graphql/executor`.
 */

/**
 * Contract types the expander can render (each reuses an already-supported detail presenter, so it
 * shows correctly). Keep in sync with `expandSpec.ts` (builder registry) and the AI response-schema
 * enum in `generateMockSpec.ts`. Grouped by the family whose presenter renders
 * them; taxonomy per `.ai-rules/knowledge/contract-types.md`.
 */
export const MOCK_CONTRACT_TYPES = [
  // EPK family (Egen pensjonskonto) + PKB — shared pension detail presenter (keyValues + holdings).
  "epk",
  "epkFleksibel",
  "epkFleksibelPkb",
  "pkb",
  // Link family (unit-linked, ProLink) — shared Link detail presenter (keyValues + savings agreement).
  "fondskonto-link",
  "garanti",
  "livrente-link",
  "ekstrapensjon",
  "ips",
  "ipo",
  "ipa-link",
  "kollektiv-livrente",
  // Alis family (individual insurance, Abakus) — shared Alis detail presenter (balance-based).
  "kapitalforsikring-alis",
  "livrente-alis",
  "ipa-alis",
  // Fripolise / FMI family (paid-up policies; the guaranteed ones surface no keyValues).
  "fripolise",
  "fmi",
  "fmi-med-garanti",
  // Bank / savings accounts — rendered in the overview's "Other savings" group (no chart/holdings).
  "bank-account",
  // Incoming contract — a pension contract on the way in; overview shows an in-transit badge and it
  // has its own detail page (no keyValues/marketValue, like the guaranteed paid-up policies).
  "incoming-contract",
  // Hybrid pension (Hybridpensjon — combination of defined-contribution and guarantee).
  // `hybrid-med-investeringsvalg` shows a value chart + holdings (like EPK); the other two are
  // guaranteed and surface no value/holdings (deep-link-only detail pages).
  "hybrid-med-investeringsvalg",
  "hybrid-med-garanti",
  "hybrid-pensjonsbevis",
  // Defined-benefit & pension-fund (ytelsespensjon / pensjonskasse) — payout/coverage based; no value
  // chart or holdings (deep-link-only detail pages). The funded and unfunded YTP families share the
  // `PensionFundYtpContract` type, discriminated by `productArea`.
  "ytp",
  "pension-fund-ytp",
  "pension-fund-unfunded-ytp",
  "pension-fund-unfunded-itp",
  "pension-fund-unfunded-ytp-under-payout",
  "public-pension",
  // Innskuddspensjon med garanti — defined-contribution with a guarantee; resolves to a Fripolise
  // presenter, so no value/holdings are shown (deep-link-only detail page).
  "innskudd-pensjon-med-garanti",
  // Employer / organization contracts. `epk-employment` shows the employment terms (employer/salary/
  // savings rate) with no chart; `ekstrapensjon-employment` is a unit-linked account that shows a value
  // chart + holdings and appears on the overview; `organization-fondskonto-link` lives on
  // OrganizationEngagement and surfaces only in the overview's Insights section (still a full
  // unit-linked detail page).
  "epk-employment",
  "ekstrapensjon-employment",
  "organization-fondskonto-link",
  // UCITS fund accounts (aksjesparekonto / investeringskonto). A distinct GraphQL type (`UCITSAccount`)
  // with its own holdings shape and no in-app detail page — they render as fund cards in the overview
  // and link out via `contractNavigation`. `ask-account` is an aksjesparekonto (accountType `"ASK"`);
  // `investor-account` is a regular fund/investor account (accountType `"SIGL"`).
  "ask-account",
  "investor-account"
] as const;
export type MockContractType = (typeof MOCK_CONTRACT_TYPES)[number];

/** Risk profiles that map onto the existing fund-mix fixtures in `builders.ts`. */
export const MOCK_RISK_PROFILES = ["aggressive", "balanced", "conservative"] as const;
export type MockRiskProfile = (typeof MOCK_RISK_PROFILES)[number];

/**
 * Investment-guidance states, mapped onto the contract's `allocationCode` by the expander. Drives the
 * investment-allocation page: `"anbefalt-pensjon"` → the "Anbefalt pensjon" recommendation card;
 * `"nedtrapping"` → the downscaling (nedtrapping) badge; `"none"` → neither (ingen nedtrapping).
 */
export const MOCK_INVESTMENT_GUIDANCE = ["anbefalt-pensjon", "nedtrapping", "none"] as const;
export type MockInvestmentGuidance = (typeof MOCK_INVESTMENT_GUIDANCE)[number];

/**
 * Network states the mock can simulate so the developer can exercise the app's non-happy-path UI:
 * `"error"` fails the matching queries (the app renders its error state), `"loading"` never resolves
 * them (loading skeletons show indefinitely) and `"slow"` resolves them after a delay. Applied by
 * the schema executor (`createSchemaExecutor`, which folds in the old simulation link); the empty
 * state needs no simulation — it is `contracts: []` (clean-slate).
 */
export const MOCK_SIMULATION_STATES = ["error", "loading", "slow"] as const;
export type MockSimulationState = (typeof MOCK_SIMULATION_STATES)[number];

/**
 * Which queries a simulation affects. Overview / account-list queries are named `fetch…` and
 * detail-page queries `get…`, so the scope maps to an operation-name prefix: `"overview"` hits only
 * the overview, `"details"` only the contract detail pages, `"all"` (default) every query.
 */
export const MOCK_SIMULATION_SCOPES = ["all", "overview", "details"] as const;
export type MockSimulationScope = (typeof MOCK_SIMULATION_SCOPES)[number];

/**
 * A forced network state for testing loading / error / slow UI. `state` is required; `scope` defaults
 * to `"all"`; `delayMs` applies to `"slow"` only (default 3000). Omit `simulate` entirely for normal
 * data — an EMPTY persona is `contracts: []`, not a simulation.
 */
export interface MockSimulation {
  state: MockSimulationState;
  scope?: MockSimulationScope;
  /** For `"slow"`: delay in milliseconds before the matching queries resolve. */
  delayMs?: number;
}

/**
 * A single pension-coverage row (dekning) — one payout line in the coverages table on the guaranteed
 * detail pages. Maps onto an `AgeCoverage`: `name` becomes the displayed coverage name, `fromAge`/
 * `toAge` the payout age window, `annualAmount` the yearly NOK payout and `lifelong` a lifelong
 * payout (no upper age). Every field is optional so the model can describe a coverage as loosely as
 * the developer does; the expander fills deterministic defaults for any it omits.
 */
export interface MockCoverage {
  /** Coverage display name, e.g. "Alderspensjon", "Uførepensjon", "Ektefellepensjon". */
  name?: string;
  /** Age the payout starts (inclusive), integer 0–120. */
  fromAge?: number;
  /** Age the payout stops (exclusive), integer 0–120. Omit for a lifelong coverage. */
  toAge?: number;
  /** Annual payout amount in NOK. */
  annualAmount?: number;
  /** When true the coverage pays out for life — no upper age (`toAge` is ignored). */
  lifelong?: boolean;
}

export interface MockContract {
  type: MockContractType;
  /** Total market value in NOK. For `bank-account` this is the account balance. */
  marketValue: number;
  /** Amount paid in (NOK). Defaults to ~80 % of `marketValue` when omitted. */
  paidIn?: number;
  /**
   * Total amount withdrawn so far (NOK). When > 0 the contract is in / through its payout phase:
   * the "Uttak" bar shows it, the value chart bends down as capital is paid out, and the
   * transactions become disbursements (money out). Defaults to 0 (accumulation phase).
   */
  paidOut?: number;
  /** Annual average return, percent. */
  returnPct?: number;
  /** Maps to an existing fund-mix / investment-profile fixture. */
  riskProfile?: MockRiskProfile;
  /**
   * Number of funds in the portfolio (e.g. "epk with 5 funds"). When set, the expander builds a
   * portfolio with exactly this many funds and matching investment-profile choices, overriding the
   * `riskProfile` fund mix. Not applicable to `fripolise` (guaranteed, no holdings).
   */
  fundCount?: number;
  /**
   * Contract age in years (default 5). Controls how far back the value chart runs — a small value
   * renders a short, shallow history (a brand-new contract); a large one a long, established one.
   * Validated to an integer 1–40.
   */
  historyYears?: number;
  /**
   * Investment guidance shown on the contract's investment-allocation page, mapped to its
   * `allocationCode`. `"anbefalt-pensjon"` shows the "Anbefalt pensjon" recommendation card;
   * `"nedtrapping"` shows the downscaling (nedtrapping) badge; `"none"` shows neither. Applies to the
   * EPK and unit-linked (Link) families that have an allocation page; ignored by families without one
   * (paid-up, defined-benefit, bank and incoming contracts). Omit to leave the allocation code unset.
   */
  investmentGuidance?: MockInvestmentGuidance;
  /**
   * Customer-supplied nickname shown on the overview card and detail header. For `bank-account` it
   * maps onto the account alias; for `ask-account`/`investor-account` onto the account name (when
   * omitted the card shows the localized product label — "Aksjesparekonto" / "Investeringskonto").
   */
  name?: string;
  /** Employer name — `epkFleksibel`, `epk-employment` and `ekstrapensjon-employment`. */
  employer?: string;
  /** Annual salary in NOK — `epkFleksibel`, `epk-employment` and `ekstrapensjon-employment`. */
  salary?: number;
  /** Basic savings rate, percent — `epkFleksibel`, `epk-employment` and `ekstrapensjon-employment`. */
  savingsRatePct?: number;
  /**
   * Whether an `epkFleksibel` carries an Ansattavtale (employment contract) — the EmploymentContractCard
   * on its detail page plus a resolvable standalone `/epk-employment` page. Unlike a plain `epk` (which
   * always shows employment), a self-managed EPK from a former employer may have none, so this is
   * off by default; set it true to include the card. Setting `employer`/`salary`/`savingsRatePct`
   * implies it. Ignored by other types.
   */
  employment?: boolean;
  /**
   * Pension coverage rows (dekninger) rendered in the coverages table on the guaranteed detail pages:
   * `fripolise`, `ytp`, `public-pension`, `hybrid-med-garanti`, `hybrid-pensjonsbevis` and
   * `pension-fund-unfunded-ytp-under-payout`. Each entry becomes an `AgeCoverage` row. When omitted the
   * page's coverages auto-mock to random values; ignored by families whose detail page shows no
   * coverages table.
   */
  coverages?: MockCoverage[];
}

/**
 * General, schema-addressed override tree — the "mock anything" escape hatch. Keyed by GraphQL type
 * name; each value is a field→value map applied on top of automatic mocking (see `applyOverrides.ts`).
 * Leaf values are arbitrary JSON; whether a type/field actually exists is validated against the real
 * schema by `validateOverrides` (this parser only checks the outer shape, staying dependency-free).
 */
export type MockOverrides = Record<string, Record<string, unknown>>;

export interface MockSpec {
  /**
   * The fictional customer themselves. `name` is a display name (currently not rendered anywhere in
   * the app). `age` (years) drives age-gated UI: in offline/dev the mock auth client turns it into the
   * user's fødselsnummer so `getUserAge()` reflects it — e.g. the investment-allocation downscaling
   * card requires age > 57. Omit `age` to keep the default mock age.
   */
  persona?: { name?: string; age?: number };
  /** High-level, polished contracts (coherent charts + deterministic deep links). */
  contracts: MockContract[];
  /**
   * When true, the overview's Insights section (organization / on-behalf-of accounts) is populated
   * from the base fixture instead of being cleared. Off by default (clean-slate).
   */
  insights?: boolean;
  /**
   * Force a network state (loading / error / slow) so the app's non-happy-path UI can be tested.
   * Absent for normal data; an EMPTY persona is `contracts: []`, not a simulation.
   */
  simulate?: MockSimulation;
  /** Low-level, general overrides against any GraphQL type/field. Absent when unused. */
  overrides?: MockOverrides;
}

export type ParseResult = { ok: true; data: MockSpec } | { ok: false; errors: string[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * Fold the model's `overridesJson` string back into the `overrides` object `parseMockSpec` expects.
 *
 * The structured-output schema cannot express an arbitrary-key map, so the general overrides tree
 * (`{ TypeName: { field: value } }`) has to travel as a JSON **string** and be parsed back on
 * arrival. Malformed JSON is ignored rather than raised: the contracts still apply, so one bad
 * override must not fail the whole turn.
 *
 * It lives here, beside the parser, rather than in `generateMockSpec.ts` with the rest of the prompt
 * because the packaged widget calls it synchronously through `pensionPack.normalizeModelSpec` — and
 * reaching into `generateMockSpec` for it would drag that module's ~10 KB of instructions into the
 * main bundle, which is the one thing the lazy import exists to prevent.
 *
 * Returns a new object rather than editing the one it was handed. The caller's value is always fresh
 * `JSON.parse` output today, so mutating it would be safe — but this is part of the `DomainPack`
 * contract now, and a normalizer that quietly rewrites its argument is a trap for the next consumer.
 */
export const normalizeModelSpec = (spec: unknown): unknown => {
  if (!isRecord(spec)) {
    return spec;
  }
  const { overridesJson, ...rest } = spec;
  if (typeof overridesJson !== "string" || !overridesJson.trim()) {
    return rest;
  }
  try {
    const parsed: unknown = JSON.parse(overridesJson);
    return isRecord(parsed) ? { ...rest, overrides: parsed } : rest;
  } catch {
    // Ignore malformed overrides JSON; the rest of the spec is still returned.
    return rest;
  }
};

/**
 * Validate and normalise an untyped value (a parsed object or a JSON string) into a `MockSpec`.
 * Unknown fields are stripped so the persisted spec stays canonical, and every problem is collected
 * into `errors` so the dev panel can show them all at once.
 */
export const parseMockSpec = (input: unknown): ParseResult => {
  let raw: unknown = input;

  if (typeof input === "string") {
    try {
      raw = JSON.parse(input);
    } catch {
      return { ok: false, errors: ["Not valid JSON."] };
    }
  }

  if (!isRecord(raw)) {
    return { ok: false, errors: ["Spec must be a JSON object."] };
  }

  const errors: string[] = [];

  // persona (optional)
  let persona: MockSpec["persona"];
  if (raw.persona !== undefined) {
    if (!isRecord(raw.persona)) {
      errors.push("`persona` must be an object.");
    } else {
      const collectedPersona: { name?: string; age?: number } = {};
      const { name, age } = raw.persona;
      if (name !== undefined && typeof name !== "string") {
        errors.push("`persona.name` must be a string.");
      } else if (typeof name === "string") {
        collectedPersona.name = name;
      }
      if (age !== undefined) {
        if (!isFiniteNumber(age) || !Number.isInteger(age) || age < 0 || age > 120) {
          errors.push("`persona.age` must be an integer between 0 and 120.");
        } else {
          collectedPersona.age = age;
        }
      }
      if (Object.keys(collectedPersona).length > 0) {
        persona = collectedPersona;
      }
    }
  }

  // contracts (optional). When given it must be an array of valid contracts. An empty array (or an
  // absent field) is allowed and represents an empty persona — the "delete all" / clean-slate state.
  const contracts: MockContract[] = [];
  if (raw.contracts !== undefined) {
    if (!Array.isArray(raw.contracts)) {
      errors.push("`contracts` must be an array.");
    } else {
      raw.contracts.forEach((entry, index) => {
        const label = `contracts[${index}]`;
        if (!isRecord(entry)) {
          errors.push(`${label} must be an object.`);
          return;
        }

        const { type } = entry;
        if (!MOCK_CONTRACT_TYPES.includes(type as MockContractType)) {
          errors.push(`${label}.type must be one of: ${MOCK_CONTRACT_TYPES.join(", ")}.`);
          return;
        }

        if (!isFiniteNumber(entry.marketValue) || entry.marketValue < 0) {
          errors.push(`${label}.marketValue must be a number >= 0.`);
          return;
        }

        const contract: MockContract = { type: type as MockContractType, marketValue: entry.marketValue };

        const numericFields = ["paidIn", "returnPct", "salary", "savingsRatePct"] as const;
        numericFields.forEach((field) => {
          const value = entry[field];
          if (value === undefined) return;
          if (!isFiniteNumber(value)) {
            errors.push(`${label}.${field} must be a number.`);
          } else {
            contract[field] = value;
          }
        });

        if (entry.riskProfile !== undefined) {
          if (!MOCK_RISK_PROFILES.includes(entry.riskProfile as MockRiskProfile)) {
            errors.push(`${label}.riskProfile must be one of: ${MOCK_RISK_PROFILES.join(", ")}.`);
          } else {
            contract.riskProfile = entry.riskProfile as MockRiskProfile;
          }
        }

        if (entry.investmentGuidance !== undefined) {
          if (!MOCK_INVESTMENT_GUIDANCE.includes(entry.investmentGuidance as MockInvestmentGuidance)) {
            errors.push(`${label}.investmentGuidance must be one of: ${MOCK_INVESTMENT_GUIDANCE.join(", ")}.`);
          } else {
            contract.investmentGuidance = entry.investmentGuidance as MockInvestmentGuidance;
          }
        }

        if (entry.fundCount !== undefined) {
          if (!isFiniteNumber(entry.fundCount) || !Number.isInteger(entry.fundCount) || entry.fundCount < 1) {
            errors.push(`${label}.fundCount must be an integer >= 1.`);
          } else {
            contract.fundCount = entry.fundCount;
          }
        }

        if (entry.paidOut !== undefined) {
          if (!isFiniteNumber(entry.paidOut) || entry.paidOut < 0) {
            errors.push(`${label}.paidOut must be a number >= 0.`);
          } else {
            contract.paidOut = entry.paidOut;
          }
        }

        if (entry.historyYears !== undefined) {
          if (
            !isFiniteNumber(entry.historyYears) ||
            !Number.isInteger(entry.historyYears) ||
            entry.historyYears < 1 ||
            entry.historyYears > 40
          ) {
            errors.push(`${label}.historyYears must be an integer between 1 and 40.`);
          } else {
            contract.historyYears = entry.historyYears;
          }
        }

        const stringFields = ["name", "employer"] as const;
        stringFields.forEach((field) => {
          const value = entry[field];
          if (value === undefined) return;
          if (typeof value !== "string") {
            errors.push(`${label}.${field} must be a string.`);
          } else {
            contract[field] = value;
          }
        });

        if (entry.employment !== undefined) {
          if (typeof entry.employment !== "boolean") {
            errors.push(`${label}.employment must be a boolean.`);
          } else {
            contract.employment = entry.employment;
          }
        }

        // coverages (optional) — an array of pension-coverage rows (dekninger). Each entry's fields are
        // all optional; the expander fills deterministic defaults for any omitted, so a coverage can be
        // described as loosely as `{}`. Only kept for the families whose detail page renders them.
        if (entry.coverages !== undefined) {
          if (!Array.isArray(entry.coverages)) {
            errors.push(`${label}.coverages must be an array.`);
          } else {
            const coverages: MockCoverage[] = [];
            entry.coverages.forEach((rawCoverage, coverageIndex) => {
              const coverageLabel = `${label}.coverages[${coverageIndex}]`;
              if (!isRecord(rawCoverage)) {
                errors.push(`${coverageLabel} must be an object.`);
                return;
              }
              const coverage: MockCoverage = {};

              if (rawCoverage.name !== undefined) {
                if (typeof rawCoverage.name !== "string") {
                  errors.push(`${coverageLabel}.name must be a string.`);
                } else {
                  coverage.name = rawCoverage.name;
                }
              }

              (["fromAge", "toAge"] as const).forEach((ageField) => {
                const value = rawCoverage[ageField];
                if (value === undefined) return;
                if (!isFiniteNumber(value) || !Number.isInteger(value) || value < 0 || value > 120) {
                  errors.push(`${coverageLabel}.${ageField} must be an integer between 0 and 120.`);
                } else {
                  coverage[ageField] = value;
                }
              });

              if (rawCoverage.annualAmount !== undefined) {
                if (!isFiniteNumber(rawCoverage.annualAmount) || rawCoverage.annualAmount < 0) {
                  errors.push(`${coverageLabel}.annualAmount must be a number >= 0.`);
                } else {
                  coverage.annualAmount = rawCoverage.annualAmount;
                }
              }

              if (rawCoverage.lifelong !== undefined) {
                if (typeof rawCoverage.lifelong !== "boolean") {
                  errors.push(`${coverageLabel}.lifelong must be a boolean.`);
                } else {
                  coverage.lifelong = rawCoverage.lifelong;
                }
              }

              coverages.push(coverage);
            });
            if (coverages.length > 0) {
              contract.coverages = coverages;
            }
          }
        }

        contracts.push(contract);
      });
    }
  }

  // insights (optional) — a top-level toggle that keeps the overview's Insights section (organization /
  // on-behalf-of accounts) populated from the base fixture instead of clearing it.
  let insights: boolean | undefined;
  if (raw.insights !== undefined) {
    if (typeof raw.insights !== "boolean") {
      errors.push("`insights` must be a boolean.");
    } else {
      insights = raw.insights;
    }
  }

  // simulate (optional) — a forced network state (loading / error / slow) for testing non-happy-path
  // UI. `state` is required and must be a known value; `scope` defaults to "all"; `delayMs` (slow
  // only) must be a non-negative number.
  let simulate: MockSimulation | undefined;
  if (raw.simulate !== undefined) {
    if (!isRecord(raw.simulate)) {
      errors.push("`simulate` must be an object.");
    } else {
      const { state, scope, delayMs } = raw.simulate;
      if (!MOCK_SIMULATION_STATES.includes(state as MockSimulationState)) {
        errors.push(`\`simulate.state\` must be one of: ${MOCK_SIMULATION_STATES.join(", ")}.`);
      } else {
        const collected: MockSimulation = { state: state as MockSimulationState };
        if (scope !== undefined) {
          if (!MOCK_SIMULATION_SCOPES.includes(scope as MockSimulationScope)) {
            errors.push(`\`simulate.scope\` must be one of: ${MOCK_SIMULATION_SCOPES.join(", ")}.`);
          } else {
            collected.scope = scope as MockSimulationScope;
          }
        }
        if (delayMs !== undefined) {
          if (!isFiniteNumber(delayMs) || delayMs < 0) {
            errors.push("`simulate.delayMs` must be a number >= 0.");
          } else {
            collected.delayMs = delayMs;
          }
        }
        simulate = collected;
      }
    }
  }

  // overrides (optional) — general schema-addressed tree. Outer shape only here; whether a type or
  // field actually exists is validated against the real schema by `validateOverrides` (needs the
  // schema, which this dependency-free parser deliberately doesn't import).
  let overrides: MockOverrides | undefined;
  if (raw.overrides !== undefined) {
    if (!isRecord(raw.overrides)) {
      errors.push("`overrides` must be an object keyed by GraphQL type name.");
    } else {
      const collected: MockOverrides = {};
      Object.entries(raw.overrides).forEach(([typeName, fields]) => {
        if (!isRecord(fields)) {
          errors.push(`overrides.${typeName} must be an object of field overrides.`);
        } else {
          collected[typeName] = fields;
        }
      });
      if (Object.keys(collected).length > 0) {
        overrides = collected;
      }
    }
  }

  // A spec with no contracts and no overrides is intentionally valid: it is an empty persona — the
  // "delete all contracts" / clean-slate state the dev panel applies to render an empty overview.

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    data: {
      ...(persona ? { persona } : {}),
      contracts,
      ...(insights ? { insights: true } : {}),
      ...(simulate ? { simulate } : {}),
      ...(overrides ? { overrides } : {})
    }
  };
};
